import { mapGetters } from "vuex";
import {
	getAllAssessments,
	getMarks,
	getPublicAssessments,
	getShareLink,
	getUserCredits,
} from "../../utils/apiFunctions";
import { trackAssessMarksViewed, trackSelfAssessPlayed } from "@/utils/posthogHelpers";
import { Capacitor } from "@capacitor/core";
import { addUserCreditValue } from "@/controllers/dashboard/asynchronous";

const COMPLETED = "completed";
const UPCOMING = "upcoming";
const ONGOING = "ongoing";
const PUBLIC = "public";
const ADS = "ads";

// Mobile Screens
const MOBILE_MAIN_SCREEN = "mobile-main-screen";
const MOBILE_VIEW_MARKS = "mobile-view-marks";
const MOBILE_TEST_DETAILS = "mobile-test-details";

//Filters
const AVERAGE_DIFFICULTY = {
	1: "Level 01",
	2: "Level 02",
	3: "Level 03",
	4: "Level 04",
	5: "Level 05",
};

const DURATION = {
	"<30": "Less than 30 mins",
	"<60": "Less than 60 mins",
	"<90": "Less than 90 mins",
	">90": "Above 90 mins",
};

const SORT_BY = {
	intDifficulty_true: "Levels(Asc)",
	intDifficulty_false: "Levels(Desc)",
	testDuration_true: "Duration(Asc)",
	testDuration_false: "Duration(Desc)",
	topicName_true: "Topics(Asc)",
	topicName_false: "Topics(Desc)",
	testName_true: "Tests(Asc)",
	testName_false: "Tests(Desc)",
};

export default {
	data() {
		return {
			loaderStatus: false,
			loaderSide: false,
			isPublic: false,
			mainLoader: false,
			loadingMore: false,
			publicSearch: "",
			search: "",
			assessShimmer: 10,
			currentTab: "",
			currentMobileScreen: "",
			isCopied: false,
			expandedAttempt: null,

			showFilter: false,
			filter: {
				intDifficulty: null,
				testDuration: null,
				topicName: [],
			},

			tempFilter: {
				intDifficulty: null,
				testDuration: null,
				topicName: [],
			},

			sortBy: {
				column: null,
				order: null,
			},

			tempSortBy: null,

			totalPages: null,
			pageCount: 20,
			pageStart: null,
			pageEnd: null,
			pageNumberToGo: null,
			total: null,
			currentPage: null,

			topicNames: [],
			publicAssessments: null,
			completedAssessments: null,
			upcomingAssessments: null,
			ongoingAssessments: null,

			showAssessmentMarks: false,
			showTestDetails: false,
			showNotifyMe: false,

			selectedAssessment: {},
			showPayment: false,
			creditData: {
				type: "",
				credits: 0,
				templateCost: 0,
			},
			customerID: null,
		};
	},
	created() {
		this.currentTab = PUBLIC;
		this.publicSearch = this.$route.query.searchQuery;
	},
	mounted() {
		window.addEventListener("message", (event) => {
			if (event.data.testSubmitted) {
				console.log("event assessment submitted");
				this.$router.go(0);
			}
		});
	},
	beforeDestroy() {
		window.removeEventListener("message");
	},
	methods: {
		async userCredits() {
			try {
				const response = await getUserCredits();
				if (response.status === 200) {
					this.creditData = response.data.data;
				}
			} catch (error) {
				console.log(error);
			}
		},
		hidePayment() {
			this.showPayment = false;
		},
		async handleAfterPayment(data) {
			if (data.type == "Success") {
				this.hidePayment();
				this.successModal("Success", "Payment successful.");
				await addUserCreditValue(this, data.data.data.paymentInfo.orderID);
				await this.userCredits();
				this.openGraphBuy = false;
			}
		},
		viewSectionReport(section) {
			this.$router.push({
				name: "AssessmentReport",
				query: {
					testID: this.selectedAssessment.testID,
					groupID: this.selectedAssessment.group.groupID,
					attemptNo: section.attemptNo,
				},
			});
		},
		async getAllAssessments(isMobile = false) {
			try {
				const res = await getAllAssessments(this.userInfo.email);

				if (res.status === 200) {
					this.completedAssessments = res.data.data.completed;
					this.upcomingAssessments = res.data.data.upcoming;
					this.ongoingAssessments = res.data.data.ongoing;

					if (!isMobile) {
						if (this.completedAssessments.length) {
							this.currentTab = COMPLETED;
						} else if (this.upcomingAssessments.length) {
							this.currentTab = UPCOMING;
						} else if (this.ongoingAssessments.length) {
							this.currentTab = ONGOING;
						} else {
							this.currentTab = ADS;
						}
					}
				} else {
					throw new Error("Failed with status: " + res.status);
				}
			} catch (err) {
				console.error(err);
				this.currentTab = ADS;
				this.completedAssessments = [];
				this.upcomingAssessments = [];
				this.ongoingAssessments = [];
			} finally {
				this.loaderSide = false;
			}
		},
		async getPublicAssessmentsLazily() {
			if (this.publicAssessments) this.loadingMore = true;
			else this.loaderStatus = true;
			try {
				const res = await getPublicAssessments({
					page: this.currentPage ? this.currentPage - 1 : 0,
					count: this.pageCount,
					email: this.userInfo.email,
					filter: {
						...this.filter,
						subCategoryID: this.$route.query.subCategory,
						categoryID: this.$route.query.category,
					},
					sortBy: this.sortBy,
					searchQuery: this.showPublic ? this.publicSearch : this.search || "",
				});

				if (res.status === 200) {
					if (!this.publicAssessments) {
						this.publicAssessments = res.data.data.data;
						this.$nextTick(() => {
							this.$refs["assessments"].addEventListener(
								"scroll",
								this.handleScroll
							);
						});
					} else {
						this.publicAssessments = [
							...this.publicAssessments,
							...res.data.data.data,
						];
					}
					this.topicNames = res.data.data.allTopicNames;
					this.total = res.data.data.total;
					this.pageStart = res.data.data.pageStart;
					this.pageEnd = res.data.data.pageEnd;
					this.totalPages = Math.ceil(this.total / this.pageCount);
				} else {
					throw new Error("Failed with status: " + res.status);
				}
			} catch (err) {
				console.error(err);
				this.publicAssessments = [];
			} finally {
				this.loadingMore = false;
				this.loaderStatus = false;
			}
		},
		async getPublicAssessments() {
			this.loaderStatus = true;
			// this.isManualClear = false;
			try {
				const res = await getPublicAssessments({
					page: this.currentPage ? this.currentPage - 1 : 0,
					count: this.pageCount,
					email: this.userInfo.email,
					filter: {
						...this.filter,
						subCategoryID: this.$route.query.subCategory,
						categoryID: this.$route.query.category,
					},
					sortBy: this.sortBy,
					searchQuery: this.showPublic ? this.publicSearch : this.search || "",
				});

				if (res.status === 200) {
					this.publicAssessments = res.data.data.data;
					const element = document.getElementById("public-assessments");
					if (element) {
						element.scrollTo({
							top: 0,
							behavior: "smooth",
						});
					}
					this.topicNames = res.data.data.allTopicNames;
					this.total = res.data.data.total;
					this.pageStart = res.data.data.pageStart;
					this.pageEnd = res.data.data.pageEnd;
					this.totalPages = Math.ceil(this.total / this.pageCount);
					// if(typeof(this.testid)=='string'){
					//   let assessment=this.publicAssessments.find(obj => obj.testID === this.testid);
					//   console.log(this.publicAssessments,"this.publicassmments")

					//   this.viewTestDetails(assessment);

					// }
				} else {
					throw new Error("Failed with status: " + res.status);
				}
			} catch (err) {
				console.error(err);
				this.publicAssessments = [];
			} finally {
				this.loaderStatus = false;
				this.loaderSide = false;
			}
		},
		async viewMarks(assessment, type) {
			if (type == "public") {
				this.isPublic = true;
			} else {
				this.isPublic = false;
			}
			this.isCopied = false;
			try {
				const payload = {
					emailID: this.userInfo.email,
					testID: assessment.testID,
					groupID: assessment.groupID,
				};
				const res = await getMarks(payload);

				this.expandedAttempt = null;
				this.selectedAssessment = Object.assign({}, res.data.data);
				if (this.selectedAssessment.shareLink) {
					if (!Capacitor.isNativePlatform()) {
						this.selectedAssessment.shareLinkWithQuery = this.appendQuery(
							this.selectedAssessment.shareLink,
							{
								key: "from",
								value: "candidate",
							}
						);
					} else {
						this.selectedAssessment.shareLinkWithQuery =
							this.selectedAssessment.shareLink;
					}
				}
				trackAssessMarksViewed({ testID: assessment.testID, groupID: assessment.groupID })				
				if (window.innerWidth >= 1024) {
					// Laptop → modal
					this.showAssessmentMarks = true;
				} else {
					// Mobile →
					// page
					this.$store.commit(
						"candidateVUEX/addSelectAssess",
						this.selectedAssessment
					);

					this.$router.push({
						name: "mobViewMarks",
						query: {
							public: true,
							from: this.$route.query.from,
							searchQuery: this.$route.query.searchQuery,
						},
					});
				}
			} catch (err) {
				console.error(err);
			} finally {
				this.loaderStatus = false;
			}
		},
		takeNewTest(assessment) {
			// Construct query parameters for the redirect page
			const query = {
				testID: assessment.testID,
				publicConfigType: assessment.publicConfigType || 0,
				isPublic: this.isPublic,
			};

			if (!this.isPublic && assessment.shareLinkWithQuery) {
				query.shareLink = assessment.shareLinkWithQuery;
			}
			let assessDetails = {
				email: this.userInfo.email,
				testID: assessment.testID,
				testName: assessment.testName
			}
			trackSelfAssessPlayed(assessDetails)

			// Create the local URL for the redirect page
			const routeData = this.$router.resolve({
				name: "AssessmentRedirect",
				query: query,
			});



			// Open the redirect page in a new tab synchronously
			window.open(routeData.href, "_blank");
		},
		attemptsLeft(assessment) {
			console.log("assessmentassessmentassessmentassessment", assessment);
			const query = {
				testID: assessment.testID,
				publicConfigType: assessment.publicConfigType || 0,
				isPublic: false,
			};

			if (assessment.shareLink) {
				query.shareLink = assessment.shareLink;
			}

			// Create the local URL for the redirect page
			const routeData = this.$router.resolve({
				name: "AssessmentRedirect",
				query: query,
			});

			// Open the redirect page in a new tab synchronously
			window.open(routeData.href, "_blank");
		},
		roundToTwo(num) {
			return Math.round(num);
		},
		getProctoringType(assessment) {
			let proctoringTypes = [];
			if (assessment.config.allowAgent) proctoringTypes.push("agent");
			if (assessment.config.allowExtension) proctoringTypes.push("extension");
			if (assessment.config.enableProctoring) proctoringTypes.push("video");
			return proctoringTypes.join("-");
		},
		toggleFilter() {
			if (!this.showFilter) {
				this.tempFilter = { ...this.filter };
				this.tempSortBy = this.sortBy.column
					? `${this.sortBy.column}_${this.sortBy.order}`
					: null;
			}
			this.showFilter = !this.showFilter;
		},
		extractSortBy(sortBy) {
			if (this.tempSortBy)
				return {
					column: sortBy.split("_")[0],
					order: sortBy.split("_")[1],
				};
			else return { column: null, order: null };
		},
		applyFilter() {
			this.loaderStatus = true;
			this.filter = { ...this.tempFilter };
			this.sortBy = this.extractSortBy(this.tempSortBy);

			if (this.currentPage === 1) {
				this.getPublicAssessments();
			} else {
				this.currentPage = 1;
			}
			this.showFilter = false;
		},
		applyFilterMobile() {
			this.loaderStatus = true;
			this.filter = { ...this.tempFilter };
			this.sortBy = this.extractSortBy(this.tempSortBy);
			let element = this.$refs["assessments"];

			if (element) {
				element.scrollTo(0, 0);
			}

			this.currentPage = 1;
			this.getPublicAssessments();
			this.showFilter = false;
		},
		resetFilter(isMobile = false) {
			if (typeof isMobile !== "boolean") isMobile = false;
			this.tempFilter = {
				intDifficulty: null,
				testDuration: null,
				topicName: [],
			};
			this.tempSortBy = null;
			if (isMobile) this.applyFilterMobile();
			else this.applyFilter();
		},
		openLink(link) {
			window.open(link, "_blank");
		},
		toDropdownOptions(options) {
			if (Array.isArray(options)) {
				return options.map((option) => ({
					value: option,
					text: option,
				}));
			} else {
				return Object.keys(options).map((key) => ({
					value: key,
					text: options[key],
				}));
			}
		},
		parseInt(value) {
			return parseInt(value);
		},
		getDate(date) {
			return new Date(date).toLocaleDateString().replaceAll("/", "-");
		},
		toggleAttempt(attemptNo) {
			this.expandedAttempt =
				this.expandedAttempt === attemptNo ? null : attemptNo;
		},
		notifyMe(assessment) {
			this.showNotifyMe = true;
		},
		isUpcoming(assessment) {
			return (
				new Date(assessment.config.openTestdt).getTime() > new Date().getTime()
			);
		},
		formatDate(date) {
			return new Date(date)
				.toLocaleString("en-IN", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})
				.replaceAll("/", "-");
		},
		formatTime(date) {
			return new Date(date)
				.toLocaleString("en-IN", {
					hour: "numeric",
					minute: "numeric",
					hour12: true,
				})
				.toUpperCase();
		},
		shortenLink(link) {
			return "https://.../" + link.split("/").slice(-1);
		},
		copyToClipboard(text) {
			this.$copyToClipboard(text);
		},
		appendQuery(link, query) {
			const [baseUrl, fragment] = link.split("#");
			//checking for fragmentation in hire3x url

			if (fragment) {
				const [path, queryString] = fragment.split("?");
				const params = new URLSearchParams(queryString || "");
				params.append(query.key, query.value);

				return `${baseUrl}#${path}?${params.toString()}`;
			} else {
				const url = new URL(link);
				url.searchParams.append(query.key, query.value);

				return url.href;
			}
		},

		viewTestDetails(assessment, type, isMobile, isPublicMob) {
			console.log("calling here", type, isMobile);
			if (type == "public") {
				this.isPublic = true;
			} else {
				this.isPublic = false;
			}
			this.isCopied = false;
			this.selectedAssessment = Object.assign({}, assessment);
			this.selectedAssessment.proctoringType =
				this.getProctoringType(assessment);
			if (this.selectedAssessment.shareLink) {
				if (!Capacitor.isNativePlatform()) {
					this.selectedAssessment.shareLinkWithQuery = this.appendQuery(
						this.selectedAssessment.shareLink,
						{
							key: "from",
							value: "candidate",
						}
					);
				} else {
					this.selectedAssessment.shareLinkWithQuery =
						this.selectedAssessment.shareLink;
				}
			}
			// this.currentMobileScreen = MOBILE_TEST_DETAILS;
			if (isMobile) {
				this.$store.commit(
					"candidateVUEX/addSelectAssess",
					this.selectedAssessment
				);
				this.$router.push({
					name: "mobAssessment",
					query: {
						public: isPublicMob,
						from: this.$route.query.from,
						searchQuery: this.$route.query.searchQuery,
					},
				});
			} else {
				this.showTestDetails = true;
			}
		},
		closeFilter() {
			this.showFilter = false;
		},
	},
	computed: {
		...mapGetters("userVUEX", {
			userInfo: "getUserInfo",
		}),
		isFiltered() {
			for (let key in this.filter) {
				if (Array.isArray(this.filter[key]) && this.filter[key].length)
					return true;
				else if (!Array.isArray(this.filter[key]) && this.filter[key])
					return true;
			}
			for (let key in this.sortBy) {
				if (this.sortBy[key]) return true;
			}
			return false;
		},
		isFilterChanged() {
			if (
				JSON.stringify(this.filter) !== JSON.stringify(this.tempFilter) ||
				JSON.stringify(this.sortBy) !==
				JSON.stringify(this.extractSortBy(this.tempSortBy))
			) {
				return true;
			}
			return false;
		},
		showMobileMainScreen() {
			return this.currentMobileScreen === MOBILE_MAIN_SCREEN;
		},
		showMobileViewMarks() {
			return this.currentMobileScreen === MOBILE_VIEW_MARKS;
		},
		showMobileTestDetails() {
			return this.currentMobileScreen === MOBILE_TEST_DETAILS;
		},
		showAds() {
			return this.currentTab === ADS;
		},
		showPublic() {
			return this.currentTab === PUBLIC;
		},
		showCompleted() {
			return this.currentTab === COMPLETED;
		},
		showUpcoming() {
			return this.currentTab === UPCOMING;
		},
		showOngoing() {
			return this.currentTab === ONGOING;
		},
		AVERAGE_DIFFICULTY() {
			return AVERAGE_DIFFICULTY;
		},
		DURATION() {
			return DURATION;
		},
		SORT_BY() {
			return SORT_BY;
		},
		MOBILE_MAIN_SCREEN() {
			return MOBILE_MAIN_SCREEN;
		},
		MOBILE_TEST_DETAILS() {
			return MOBILE_TEST_DETAILS;
		},
		MOBILE_VIEW_MARKS() {
			return MOBILE_VIEW_MARKS;
		},
		PUBLIC() {
			return PUBLIC;
		},
		COMPLETED() {
			return COMPLETED;
		},
		UPCOMING() {
			return UPCOMING;
		},
		ONGOING() {
			return ONGOING;
		},
		...mapGetters("userVUEX", {
			userInfo: "getUserInfo",
		}),
	},
};
